
import { 
  getAdmins, 
  createAdmin, 
  updateAdmin, 
  deleteAdmin,
  clearAllAdminData,
  getAdminStorageStats 
} from '../db/admins';
import { Admin } from '@/types/admin.types';
import { toast } from 'sonner';

export class AdminBackupService {
  async exportAdmins(): Promise<Admin[]> {
    try {
      console.log('Starting admin data export...');
      const admins = await getAdmins();
      console.log(`Exported ${admins.length} admins`);
      return Array.isArray(admins) ? admins : [];
    } catch (error) {
      console.error('Error exporting admins:', error);
      throw new Error('Failed to export admin data');
    }
  }

  async importAdmins(admins: Admin[], options: { overwrite?: boolean } = {}): Promise<void> {
    try {
      console.log(`Starting admin data import for ${admins.length} admins...`);
      
      if (options.overwrite) {
        console.log('Clearing existing admin data...');
        await clearAllAdminData();
      }

      let successCount = 0;
      let errorCount = 0;

      for (const admin of admins) {
        try {
          const { id, createdAt, updatedAt, ...adminData } = admin;
          await createAdmin(adminData);
          successCount++;
        } catch (error) {
          console.error(`Failed to import admin ${admin.firstName} ${admin.lastName}:`, error);
          errorCount++;
        }
      }

      console.log(`Admin import completed: ${successCount} successful, ${errorCount} failed`);
      
      if (errorCount > 0) {
        toast.warning(`Admin import completed with ${errorCount} errors. ${successCount} admins imported successfully.`);
      } else {
        toast.success(`Successfully imported ${successCount} admins`);
      }
    } catch (error) {
      console.error('Error importing admins:', error);
      throw new Error('Failed to import admin data');
    }
  }

  async getBackupInfo(): Promise<{
    totalAdmins: number;
    totalSize: number;
    lastBackup?: string;
  }> {
    try {
      const admins = await getAdmins();
      const stats = await getAdminStorageStats();
      
      const storageUsedStr = stats.storageUsed;
      let totalSize = 0;
      
      if (storageUsedStr && storageUsedStr !== '0 KB') {
        const match = storageUsedStr.match(/^([\d.]+)\s*KB$/);
        if (match) {
          totalSize = parseFloat(match[1]) * 1024;
        }
      }
      
      return {
        totalAdmins: admins.length,
        totalSize: totalSize,
        lastBackup: localStorage.getItem('last_admin_backup') || undefined
      };
    } catch (error) {
      console.error('Error getting admin backup info:', error);
      return {
        totalAdmins: 0,
        totalSize: 0
      };
    }
  }
}

export const adminBackupService = new AdminBackupService();

export const createAdminBackup = async (): Promise<string> => {
  try {
    console.log('Starting admin backup creation...');
    const admins = await adminBackupService.exportAdmins();
    
    if (!Array.isArray(admins)) {
      throw new Error('Invalid admins data format');
    }
    
    const backupId = `admin_backup_${Date.now()}`;
    const backupData = {
      id: backupId,
      timestamp: new Date().toISOString(),
      admins: admins,
      size: JSON.stringify(admins).length,
      description: `Admin backup created on ${new Date().toLocaleString()}`
    };

    // Save to localStorage for local backup list
    try {
      const existingBackups = JSON.parse(localStorage.getItem('admin_backups') || '[]');
      existingBackups.push(backupData);
      localStorage.setItem('admin_backups', JSON.stringify(existingBackups));
      localStorage.setItem('last_admin_backup', new Date().toISOString());
    } catch (storageError) {
      console.warn('Could not save backup to localStorage:', storageError);
    }

    // Create and download backup file
    const backupJson = JSON.stringify(backupData, null, 2);
    const blob = new Blob([backupJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `admins-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`Admin backup created successfully: ${admins.length} admins`);
    toast.success(`Admin backup created successfully (${admins.length} admins)`);
    return backupId;
  } catch (error) {
    console.error('Error creating admin backup:', error);
    toast.error('Failed to create admin backup: ' + (error instanceof Error ? error.message : 'Unknown error'));
    throw error;
  }
};

export const restoreAdminBackup = async (file: File): Promise<boolean> => {
  try {
    const text = await file.text();
    const backupData = JSON.parse(text);
    const admins = backupData.admins || backupData.data?.admins || [];
    
    if (!Array.isArray(admins)) {
      throw new Error('Invalid backup format: admins data not found');
    }
    
    await adminBackupService.importAdmins(admins, { overwrite: true });
    return true;
  } catch (error) {
    console.error('Error restoring admin backup:', error);
    toast.error('Failed to restore admin backup: ' + (error instanceof Error ? error.message : 'Unknown error'));
    return false;
  }
};

export const listAdminBackups = (): any[] => {
  try {
    return JSON.parse(localStorage.getItem('admin_backups') || '[]');
  } catch (error) {
    console.error('Error listing admin backups:', error);
    return [];
  }
};

export const restoreFromLocalAdminBackup = async (backupId: string): Promise<boolean> => {
  try {
    const backups = listAdminBackups();
    const backup = backups.find(b => b.id === backupId);
    if (!backup) {
      toast.error('Backup not found');
      return false;
    }

    await adminBackupService.importAdmins(backup.admins, { overwrite: true });
    return true;
  } catch (error) {
    console.error('Error restoring from local admin backup:', error);
    toast.error('Failed to restore admin backup');
    return false;
  }
};
